import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { Btn, LabeledSelect } from "./Ui";

const FIELDS = [
  "language", "beneficiary_name", "dob", "age", "contact_name", "relationship",
  "relationship_other", "phone", "service", "service_other", "assessment_type",
  "assessment_other", "referral_source", "referral_other", "preferred_contact_time",
  "preferred_time_specific",
];

function Text({ label, id, value, onChange, error, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      <input id={id} className="emerge-input !h-10 text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} {...rest} />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Supervisor-only correction of the intake details.
 *
 * Only fields the supervisor actually touched are sent, and the server merges
 * them onto the stored submission before validating the result with the same
 * model the public form uses - so an edit can never produce a record a client
 * could not have submitted.
 */
export function EditSubmissionDialog({ open, onOpenChange, submission, onSaved }) {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState(null);

  useEffect(() => {
    if (!open || !submission) return;
    const init = {};
    FIELDS.forEach((f) => (init[f] = submission[f] ?? ""));
    init.ageMode = submission.dob ? "dob" : "age";
    setForm(init);
    setReason("");
    setFieldError(null);
  }, [open, submission]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setFieldError(null); };

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {};
      FIELDS.forEach((f) => {
        const next = form[f] === "" ? null : form[f];
        const prev = submission[f] ?? null;
        if (next !== prev) payload[f] = next;
      });
      // Age and date of birth are mutually exclusive; send only the chosen one.
      if (form.ageMode === "dob") { delete payload.age; if (form.dob) payload.dob = form.dob; }
      else { delete payload.dob; if (form.age !== "" && form.age != null) payload.age = Number(form.age); }
      if (reason.trim()) payload.edit_reason = reason.trim();
      if (Object.keys(payload).filter((k) => k !== "edit_reason").length === 0) {
        throw new Error("Nothing has been changed");
      }
      return (await api.patch(`/submissions/${submission.id}`, payload)).data;
    },
    onSuccess: (data) => {
      toast.success(data.changed ? "Submission updated" : "No changes to save");
      ["submissions", "submission", "stats", "appointments", "audit"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] })
      );
      onSaved?.(data);
      onOpenChange(false);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail?.field) {
        setFieldError(detail);
        toast.error(detail.message);
      } else toast.error(err?.message === "Nothing has been changed" ? err.message : errorMessage(err));
    },
  });

  const errFor = (f) => (fieldError?.field === f ? fieldError.message : null);
  const dict = (name) => Object.entries(meta?.[name] || {});

  if (!submission) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white p-0" data-testid="edit-submission-dialog">
        <DialogHeader className="border-b border-border px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Pencil className="h-5 w-5 text-primary" /> Edit submission
          </DialogTitle>
          <DialogDescription>
            Correct the details recorded for <strong className="text-foreground">{submission.beneficiary_name}</strong>. Every change is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[65vh] gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Text label="Beneficiary name" id="ed-ben" value={form.beneficiary_name} onChange={(v) => set({ beneficiary_name: v })} error={errFor("beneficiary_name")} maxLength={120} />
          <Text label="Contact person" id="ed-contact" value={form.contact_name} onChange={(v) => set({ contact_name: v })} error={errFor("contact_name")} maxLength={120} />

          <div>
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Age given as</span>
            <div className="flex gap-4 pt-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" checked={form.ageMode === "dob"} onChange={() => set({ ageMode: "dob" })} className="h-4 w-4 accent-[#2f5d83]" /> Date of birth
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" checked={form.ageMode === "age"} onChange={() => set({ ageMode: "age" })} className="h-4 w-4 accent-[#2f5d83]" /> Age
              </label>
            </div>
          </div>
          {form.ageMode === "dob" ? (
            <Text label="Date of birth" id="ed-dob" type="date" value={form.dob} onChange={(v) => set({ dob: v })} error={errFor("dob")} />
          ) : (
            <Text label="Age" id="ed-age" type="number" min={0} max={120} value={form.age} onChange={(v) => set({ age: v })} error={errFor("age")} />
          )}

          <Text label="Phone" id="ed-phone" value={form.phone} onChange={(v) => set({ phone: v })} error={errFor("phone")} dir="ltr" />
          <LabeledSelect label="Language" id="ed-lang" value={form.language} onChange={(e) => set({ language: e.target.value })}>
            {dict("languages").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </LabeledSelect>

          <LabeledSelect label="Relationship" id="ed-rel" value={form.relationship} onChange={(e) => set({ relationship: e.target.value })}>
            {dict("relationships").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </LabeledSelect>
          {form.relationship === "other" ? (
            <Text label="Relationship (other)" id="ed-rel-o" value={form.relationship_other} onChange={(v) => set({ relationship_other: v })} error={errFor("relationship_other")} maxLength={80} />
          ) : <div />}

          <LabeledSelect label="Service requested" id="ed-service" value={form.service} onChange={(e) => set({ service: e.target.value })}>
            {dict("services").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </LabeledSelect>
          {form.service === "other" ? (
            <Text label="Service (other)" id="ed-service-o" value={form.service_other} onChange={(v) => set({ service_other: v })} error={errFor("service_other")} maxLength={120} />
          ) : <div />}

          {form.service === "assessment" && (
            <>
              <LabeledSelect label="Assessment type" id="ed-assess" value={form.assessment_type} onChange={(e) => set({ assessment_type: e.target.value })}>
                <option value="">Select...</option>
                {dict("assessment_types").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </LabeledSelect>
              {form.assessment_type === "other" ? (
                <Text label="Assessment (other)" id="ed-assess-o" value={form.assessment_other} onChange={(v) => set({ assessment_other: v })} error={errFor("assessment_other")} maxLength={120} />
              ) : <div />}
            </>
          )}

          <LabeledSelect label="Referral source" id="ed-ref" value={form.referral_source} onChange={(e) => set({ referral_source: e.target.value })}>
            {dict("referral_sources").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </LabeledSelect>
          {form.referral_source === "other" ? (
            <Text label="Referral (other)" id="ed-ref-o" value={form.referral_other} onChange={(v) => set({ referral_other: v })} error={errFor("referral_other")} maxLength={120} />
          ) : <div />}

          <LabeledSelect label="Preferred contact time" id="ed-time" value={form.preferred_contact_time} onChange={(e) => set({ preferred_contact_time: e.target.value })}>
            <option value="">No preference</option>
            {dict("contact_times").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </LabeledSelect>
          <Text label="Specific time (optional)" id="ed-time-s" type="time" value={form.preferred_time_specific} onChange={(v) => set({ preferred_time_specific: v })} error={errFor("preferred_time_specific")} dir="ltr" />

          <div className="sm:col-span-2">
            <Text label="Reason for the change (optional)" id="ed-reason" value={reason} onChange={setReason} maxLength={300} placeholder="e.g. phone number corrected during the call" />
            <p className="mt-1 text-xs text-muted-foreground">Stored in the audit log next to the change.</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Btn variant="secondary" onClick={() => onOpenChange(false)} data-testid="edit-submission-cancel">Cancel</Btn>
          <Btn onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="edit-submission-save">
            {mutation.isPending && <Loader2 className="animate-spin" />} Save changes
          </Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
